import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../api'

// Types
export interface Episode {
  id: number
  moduleId: number
  number: number
  title: string
  description?: string
  videoUrl?: string
  thumbnailUrl?: string
  workbookUrl?: string
  duration?: number
  order: number
  status: 'draft' | 'published' | 'archived'
  createdAt: string
  updatedAt: string
  module?: {
    id: number
    title: string
    courseId: number
    course?: {
      id: number
      title: string
      slug: string
      companyId?: string | null
      shared?: boolean
    }
  }
}

// Match com o enum episodes_status do Prisma (back).
// 'coming_soon' adicionado em 2026-05-08 — sempre aceito pelo back via
// PATCH /episodes/:id/status, faltava só no DTO do front.
export type EpisodeStatusDTO = 'draft' | 'published' | 'archived' | 'coming_soon'

export interface CreateEpisodeDTO {
  moduleId: number
  number: number
  title: string
  description?: string
  videoUrl?: string
  thumbnailUrl?: string
  workbookUrl?: string
  workbookCoverUrl?: string
  duration?: number
  order?: number
  status?: EpisodeStatusDTO
}

export interface UpdateEpisodeDTO {
  number?: number
  title?: string
  description?: string
  videoUrl?: string
  thumbnailUrl?: string
  workbookUrl?: string
  workbookCoverUrl?: string
  duration?: number
  order?: number
  status?: EpisodeStatusDTO
}

// Hooks
export function useAdminEpisodes(params?: { moduleId?: number; courseId?: number }) {
  const queryKey = ['admin-episodes']
  if (params?.moduleId) queryKey.push('module', params.moduleId.toString())
  if (params?.courseId) queryKey.push('course', params.courseId.toString())

  return useQuery({
    queryKey,
    queryFn: async () => {
      const queryParams = new URLSearchParams()
      if (params?.moduleId) queryParams.append('moduleId', params.moduleId.toString())
      if (params?.courseId) queryParams.append('courseId', params.courseId.toString())

      const url = queryParams.toString() ? `/episodes?${queryParams.toString()}` : '/episodes'
      const response = await api.get<Episode[]>(url)
      return response.data
    },
  })
}

export function useCreateEpisode() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateEpisodeDTO) => {
      const response = await api.post<Episode>('/episodes', data)
      return response.data
    },
    onSuccess: () => {
      // Invalidate and refetch immediately
      queryClient.invalidateQueries({ queryKey: ['admin-episodes'] })
      queryClient.refetchQueries({ queryKey: ['admin-episodes'] })
    },
  })
}

export function useUpdateEpisode() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateEpisodeDTO }) => {
      const response = await api.put<Episode>(`/episodes/${id}`, data)
      return response.data
    },
    onSuccess: () => {
      // Invalidate and refetch immediately
      queryClient.invalidateQueries({ queryKey: ['admin-episodes'] })
      queryClient.refetchQueries({ queryKey: ['admin-episodes'] })
    },
  })
}

export function useDeleteEpisode() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/episodes/${id}`)
    },
    onSuccess: () => {
      // Invalidate and refetch immediately
      queryClient.invalidateQueries({ queryKey: ['admin-episodes'] })
      queryClient.refetchQueries({ queryKey: ['admin-episodes'] })
    },
  })
}

export function useUpdateEpisodeStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      status,
    }: {
      id: number
      status: 'draft' | 'published' | 'archived'
    }) => {
      const response = await api.patch<Episode>(`/episodes/${id}/status`, { status })
      return response.data
    },
    onSuccess: () => {
      // Invalidate and refetch immediately
      queryClient.invalidateQueries({ queryKey: ['admin-episodes'] })
      queryClient.refetchQueries({ queryKey: ['admin-episodes'] })
    },
  })
}
